// Library Methods
import { useTranslation } from "react-i18next";
import React, { useState } from "react";

// Components
import { Box, Divider, Grid, Typography } from "@mui/material";
import ExamCardButton from "../ui/ExamCardButton";
import ExamCardToggles from "../ui/ExamCardToggles";
import WhichEyeSection from "../ui/WhichEyeSection";
import TitleWithTooltip from "../../../../../components/UI/TitleWithTooltip";
import SelectDropdown from "../../../../../components/UI/SelectDropdown";
import ExamCardContainer from "../ui/ExamCardContainer";
import ExamCardHeader from "../ui/ExamCardHeader";

// Utilities
import {
  DEFAULT_STARTING_ROW_INDEX,
  positionTypes,
  visualAcuityRowOptions,
} from "../../../../../enums";
import useClinic from "../../../../../hooks/useClinic";

const VisualAcuityCard = ({
  label,
  examAlgorithm,
  selectedExamCards,
  updateExamCardProperty,
  onChangeEye,
  onChangeSectionOrder,
  onToggleCard,
}) => {
  // internationalization
  const { t } = useTranslation();

  // clinic context
  const { clinicLicensing } = useClinic();

  const { near, far } = positionTypes;

  const defaultVisualAcuity = {
    examFieldType: examAlgorithm,
    examType: "TUMBLING_E",
    startingRowIndex: DEFAULT_STARTING_ROW_INDEX,
    eyeCheckboxes: { right: true, left: true },
    position: far,
    binocular: true,
    sectionOrder: { right: 0, left: 1 },
    optionalToggles: {
      requiresTutorial: true,
    },
  };

  const selectedExamCard = selectedExamCards.find(
    (examCard) => examCard.examFieldType === examAlgorithm
  );
  const examCardInfo = selectedExamCard ?? defaultVisualAcuity;
  const isActive = Boolean(selectedExamCard);

  const {
    startingRowIndex: initialRowIndex,
    examType,
    eyeCheckboxes,
    sectionOrder,
    optionalToggles,
    position,
    binocular,
  } = examCardInfo;

  const [selectedRowIndex, setSelectedRowIndex] = useState(initialRowIndex);

  const textStyle = {
    color: isActive ? "#FFF" : "#5B6D7A",
    fontSize: "14px",
  };

  return (
    <ExamCardContainer isActive={isActive}>
      <ExamCardHeader
        label={label}
        isActive={isActive}
        onChange={(e) => {
          onToggleCard(e, defaultVisualAcuity);
        }}
      />
      <Grid item container display="flex" flexDirection="column" gap="8px">
        {/* visual acuity type */}
        <Grid item display="flex" flexDirection="column" gap="8px">
          <TitleWithTooltip
            title={t("exams_table_type")}
            tooltip={t("more_available_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid container item display="flex" gap="8px">
            <Grid container item gap="8px">
              <Grid item xs>
                <ExamCardButton
                  isSelected={examType === "TUMBLING_E"}
                  updateValue={() =>
                    updateExamCardProperty("examType", "TUMBLING_E")
                  }
                  label="Tumbling E"
                  isDisabled={!isActive}
                />
              </Grid>
              {clinicLicensing?.canUseBaileyLovieExams && (
                <Grid item xs>
                  <ExamCardButton
                    isSelected={examType === "BAILEY_LOVIE"}
                    updateValue={() =>
                      updateExamCardProperty("examType", "BAILEY_LOVIE")
                    }
                    label="Bailey Lovie"
                    isDisabled={!isActive}
                  />
                </Grid>
              )}
            </Grid>

            <Grid container item gap="8px">
              <Grid item xs>
                <ExamCardButton
                  isSelected={examType === "LANDOLT_C"}
                  updateValue={() =>
                    updateExamCardProperty("examType", "LANDOLT_C")
                  }
                  label="Landolt C"
                  isDisabled={!isActive}
                />
              </Grid>
              <Grid item xs>
                <ExamCardButton
                  isSelected={examType === "SNELLEN"}
                  updateValue={() =>
                    updateExamCardProperty("examType", "SNELLEN")
                  }
                  label="Snellen"
                  isDisabled={!isActive}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* position */}
        <Grid item display="flex" flexDirection="column" gap="8px">
          <TitleWithTooltip
            title={t("exams_table_position")}
            tooltip={t("more_available_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid container item display="flex" gap="8px">
            <Grid item xs>
              <ExamCardButton
                isSelected={position === near}
                updateValue={() => updateExamCardProperty("position", near)}
                label={t("word_near")}
                isDisabled={!isActive}
              />
            </Grid>
            <Grid item xs>
              <ExamCardButton
                isSelected={position === far}
                updateValue={() => updateExamCardProperty("position", far)}
                label={t("word_far")}
                isDisabled={!isActive}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Starting index */}
        <Grid item display="flex" flexDirection="column" gap="8px">
          <TitleWithTooltip
            title={t("starting_row_index")}
            tooltip={t("starting_row_index_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <SelectDropdown
            value={visualAcuityRowOptions[selectedRowIndex]}
            dropdownOptions={visualAcuityRowOptions}
            handleChange={(event) => {
              const selectedIndex = visualAcuityRowOptions.findIndex(
                (val) => val === event.target.value
              );
              if (selectedIndex > -1) {
                setSelectedRowIndex(selectedIndex);
                updateExamCardProperty("startingRowIndex", selectedIndex);
              }
            }}
            disabled={!isActive}
          />
        </Grid>

        {/* eye */}
        <Grid item display="flex" flexDirection="column" gap="8px">
          <TitleWithTooltip
            title={t("word_eye")}
            tooltip={t("which_eye_test_tooltip")}
            titleStyle={textStyle}
            tooltipStyle={textStyle}
          />

          <Grid container item display="flex" gap="8px">
            <Grid item xs>
              <ExamCardButton
                isSelected={!binocular}
                updateValue={() => updateExamCardProperty("binocular", false)}
                label={t("word_monocular")}
                isDisabled={!isActive}
              />
            </Grid>
            <Grid item xs>
              <ExamCardButton
                isSelected={binocular}
                updateValue={() => updateExamCardProperty("binocular", true)}
                label={t("word_binocular")}
                isDisabled={!isActive}
              />
            </Grid>
          </Grid>
        </Grid>

        <WhichEyeSection
          isActive={isActive}
          isRightFirst={sectionOrder.right === 0}
          eyeCheckboxes={eyeCheckboxes}
          onChangeSectionOrder={onChangeSectionOrder}
          onChangeEye={onChangeEye}
          isHidden={binocular}
        />

        <Box height={{ xs: "0px", sm: "159px" }} />

        <Divider color="#5B6D7A" />

        <Grid display="flex" flexDirection="column">
          <ExamCardToggles
            isActive={isActive}
            optionalToggles={optionalToggles}
            updateExamCardProperty={updateExamCardProperty}
            showToggles={defaultVisualAcuity.optionalToggles}
          />
        </Grid>

        <Grid item textAlign="center">
          <Typography color={isActive ? "#FFF" : "#5B6D7A"} fontSize="14px">
            {t("new_test")}
          </Typography>
          <Typography color={isActive ? "#FFF" : "#5B6D7A"} fontSize="14px">
            {t("try_it_out")}
          </Typography>
        </Grid>
      </Grid>
    </ExamCardContainer>
  );
};

export default VisualAcuityCard;
